/**
 * Field Type Mapping Utilities
 *
 * Maps YAML py_type to Ant Design component.
 * Uses ONLY fields that exist in backend FieldDefinition.
 */

import type { FieldMetadata } from '@/services/metadataService'
import type { FieldComponentMapping } from '../types'

/**
 * Map YAML py_type to Ant Design component.
 * component_type (if set) takes absolute precedence over py_type inference.
 */
export function getFieldComponent(metadata: FieldMetadata): FieldComponentMapping {
  // Check explicit component_type first (takes precedence over py_type inference)
  if (metadata.component_type) {
    switch (metadata.component_type) {
      case 'input':
        return {
          component: 'Input',
          props: { maxLength: metadata.max_length },
        }
      case 'textarea':
        return {
          component: 'TextArea',
          props: { maxLength: metadata.max_length },
        }
      case 'password':
        return {
          component: 'Input',
          props: { type: 'password' },
        }
      case 'hidden':
        return { component: 'Hidden' }
      case 'select':
        return { component: 'Select' }
      case 'radio':
        return { component: 'RadioGroup' }
      case 'switch':
        return {
          component: 'Switch',
          valuePropName: 'checked',
        }
      case 'checkbox':
        return {
          component: 'Checkbox',
          valuePropName: 'checked',
        }
      case 'datepicker':
        return {
          component: 'DatePicker',
          props: { showTime: true },
        }
      case 'inputnumber':
        return { component: 'InputNumber' }
    }
  }

  // Fall back to py_type inference
  const { py_type } = metadata

  // Pure py_type mapping (NO field name heuristics)
  switch (py_type) {
    case 'bool':
      return {
        component: 'Switch',
        valuePropName: 'checked',
      }

    case 'integer':
    case 'int':
      return {
        component: 'InputNumber',
        props: {
          step: 1,
          precision: 0,
        },
      }

    case 'numeric':
    case 'float':
    case 'Decimal':
      return {
        component: 'InputNumber',
        props: {
          step: 0.01,
        },
      }

    case 'date':
    case 'datetime':
    case 'timestamp':
    case 'timestamptz':
      return {
        component: 'DatePicker',
        props: {
          showTime: py_type !== 'date',
        },
      }

    case 'string':
    case 'str':
      // Enum fields → Select
      if (metadata.allowed_values && metadata.allowed_values.length > 0) {
        return { component: 'Select' }
      }

      // Default string → Input
      return {
        component: 'Input',
        props: {
          maxLength: metadata.max_length,
        },
      }

    case 'uuid':
      return {
        component: 'Input',
        props: {
          disabled: true, // UUIDs shouldn't be edited
        },
      }

    case 'json':
    case 'jsonb':
      // JSON fields use a specialized viewer component
      return { component: 'JsonViewer' }

    default:
      // Fallback → Input
      return { component: 'Input' }
  }
}

/**
 * Complete py_type to UI type mapping
 * (for reference and debugging)
 */
export const PY_TYPE_MAP: Record<string, string> = {
  // Text types
  string: 'Text',
  str: 'Text',
  uuid: 'Text',

  // Numeric types
  integer: 'Number',
  int: 'Number',
  float: 'Number',
  numeric: 'Number',
  Decimal: 'Number',

  // Boolean
  bool: 'Boolean',

  // Date/Time types
  date: 'Date',
  datetime: 'Date',
  timestamp: 'Date',
  timestamptz: 'Date',
}
